@if (survey) {
  <div class="container-fluid mb-5">
    <div class="dashboard-card shadow-sm my-4 border-primary">
      @if (surveyIsPrivate) {
        <div class="card-header bg-white border-bottom p-4 card-header-top">
          <div class="row align-items-start">
            <div class="col-md-7">
              <h4 class="dashboard-card-title text-primary-purple mb-1 d-flex align-items-center">
                <fa-icon [icon]="faClipboardList" class="dashboard-title-icon"></fa-icon>
                {{ 'components.responses.responses' | translate }}
              </h4>
              <p class="text-muted mb-4">View submitted answers, track response rates, and manage when participants can submit.</p>
              <div class="d-flex stats-row">
                <div class="stat-item mr-5">
                  <h2 class="mb-0 font-weight-bold text-primary-purple">{{ survey?.members?.length || 0 }}</h2>
                  <small class="stat-label">{{ "generic.total-members" | translate }}</small>
                </div>
                <div class="stat-item mr-5">
                  <h2 class="mb-0 font-weight-bold text-info">{{ participantsCount }}</h2>
                  <small class="stat-label">{{ "generic.participants" | translate }}</small>
                </div>
                <div class="stat-item mr-5">
                  <h2 class="mb-0 font-weight-bold text-success">{{ answersSent?.length }}</h2>
                  <small class="stat-label">{{ "components.responses.responses" | translate }}</small>
                </div>
              </div>
            </div>
            @if (chartOptions) {
              <div class="col-md-5 d-flex justify-content-end">
                <div class="chart-wrapper" style="height: 200px; width: 100%; max-width: 280px;">
                  <div echarts [options]="chartOptions" style="height: 100%; width: 100%;"></div>
                </div>
              </div>
            }
          </div>
        </div>
      }
      <div class="card-body p-0">
        <div class="row justify-content-center card-details-buttons">
          <div class="col-12 d-flex justify-content-between align-items-center">
            <div class="response-label-container">
              @if (survey?.answers_sent === 0) {
                <span class="text-muted">{{ 'pages.surveys.responses.no-responses' | translate }}</span>
              }
            </div>
              <div class="d-flex align-items-center">
                @if (survey?.answers_sent > 0) {
                  <button mat-stroked-button color="primary" class="mr-3 d-flex align-items-center" (click)="openReports()"
                    [disabled]="loading" [class.report-button-busy]="reportIsActive || reportStarting"
                    [attr.aria-busy]="reportIsActive || reportStarting"
                    [matTooltip]="'components.survey-report.reports-tooltip' | translate">
                    <mat-icon class="mr-2" [class.report-button-spinning]="reportIsActive || reportStarting">{{ (reportIsActive || reportStarting) ? 'autorenew' : 'slideshow' }}</mat-icon>
                    {{ reportIsActive ? reportStatusLabel : (reportStarting ? ('components.survey-report.preparing' | translate) : ('components.survey-report.reports' | translate)) }}
                  </button>
                  <button mat-stroked-button color="primary" class="mr-3 d-flex align-items-center" (click)="exportAllResponses()" [disabled]="loading">
                    <mat-icon class="mr-2">get_app</mat-icon>
                    {{ "generic.export-survey-results" | translate }}
                  </button>
                }
                @if (!survey?.is_revised) {

                <mat-button-toggle-group name="survey_status" [(ngModel)]="survey.status" (ngModelChange)="onChangeStatus($event)"
                  [hideMultipleSelectionIndicator]="true"
                  [hideSingleSelectionIndicator]="true"
                  style="height: 38px; align-items: center;"
                  *appHasRole="['OWNER', 'EDITOR']; role: currentUserInTeam?.role">
                  <mat-button-toggle
                    [value]="'EVALUATING'"
                    [ngClass]="{'bg-success text-white': survey.status === 'EVALUATING'}">
                    {{ "generic.accept-answers" | translate }}
                  </mat-button-toggle>
                  <mat-button-toggle
                    [value]="'CLOSED'"
                    [ngClass]="{'bg-danger text-white': survey.status === 'CLOSED'}">
                    {{ "generic.block-answers" | translate }}
                  </mat-button-toggle>
                </mat-button-toggle-group>
                }
              </div>
          </div>
        </div>

        <div class="px-4 py-3 border-top">
          <!-- Question-Level Filters Section -->
          <div class="mb-4">
            <h6 class="text-primary-purple font-weight-bold mb-3 d-flex align-items-center">
              <mat-icon class="mr-2">filter_list</mat-icon>
              {{ 'generic.filters' | translate }}
            </h6>

            <div class="row g-3">
              <!-- Search Bar -->
              <div class="col-md-6 col-lg-3">
                <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
                  <mat-label>{{ 'generic.buttons.search' | translate }}</mat-label>
                  <mat-icon matPrefix class="mr-2 text-muted" style="vertical-align: middle;">search</mat-icon>
                  <input matInput type="text" [ngModel]="filters.search" (ngModelChange)="onSearchChange($event)" placeholder="{{ 'generic.search-for' | translate }}...">
                  @if (filters.search) {
                    <button mat-icon-button matSuffix (click)="clearSearch()" type="button">
                      <mat-icon>close</mat-icon>
                    </button>
                  }
                </mat-form-field>
              </div>

              <!-- Question Typology Dropdown -->
              <div class="col-md-6 col-lg-3">
                <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
                  <mat-label>{{ 'generic.typology' | translate }}</mat-label>
                  <mat-select [(ngModel)]="filters.typologies" (selectionChange)="onTypologyChange()" multiple placeholder="{{ 'generic.all' | translate }}">
                    <mat-select-trigger>
                      {{ getSelectedTypologiesLabel() }}
                    </mat-select-trigger>
                    @for (option of typologyOptions; track option.value) {
                      <mat-option [value]="option.value">
                        {{ option.label | translate }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>

              <!-- Required/Optional Dropdown -->
              <div class="col-md-6 col-lg-3">
                <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
                  <mat-label>{{ 'generic.answers.mandatory' | translate }} / {{ 'generic.answers.just-suggestion' | translate }}</mat-label>
                  <mat-select [(ngModel)]="filters.required" (selectionChange)="onRequiredChange()">
                    <mat-option value="">{{ 'generic.all' | translate }}</mat-option>
                    <mat-option value="true">{{ 'generic.answers.mandatory' | translate }}</mat-option>
                    <mat-option value="false">{{ 'generic.answers.just-suggestion' | translate }}</mat-option>
                  </mat-select>
                </mat-form-field>
              </div>

              <!-- Decision Status Dropdown -->
              <div class="col-md-6 col-lg-3">
                <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
                  <mat-label>{{ 'generic.evaluation' | translate }}</mat-label>
                  <mat-select [(ngModel)]="filters.final_decision" (selectionChange)="onDecisionChange()">
                    <mat-option value="">{{ 'generic.all' | translate }}</mat-option>
                    @for (option of decisionOptions; track option.value) {
                      <mat-option [value]="option.value">
                        {{ option.label | translate }}
                      </mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>

              <!-- Section Dropdown -->
              @if (getSections().length > 0) {
                <div class="col-md-6 col-lg-3">
                  <mat-form-field appearance="outline" class="w-100" subscriptSizing="dynamic">
                    <mat-label>{{ 'generic.section' | translate }}</mat-label>
                    <mat-select [(ngModel)]="filters.section" (selectionChange)="onSectionChange()">
                      <mat-option value="">{{ 'generic.all' | translate }}</mat-option>
                      @for (sec of getSections(); track sec) {
                        <mat-option [value]="sec">
                          {{ sec }}
                        </mat-option>
                      }
                    </mat-select>
                  </mat-form-field>
                </div>
              }
            </div>
          </div>

          <!-- Divider & Participant Filters -->
          @if (survey?.team_member_custom_fields && survey.team_member_custom_fields.length > 0) {
            <h6 class="text-primary-purple font-weight-bold mb-3 mt-4 border-top pt-3 d-flex align-items-center">
              <mat-icon class="mr-2">people</mat-icon>
              {{ 'generic.filters' | translate }} {{ 'generic.users' | translate | lowercase }}
            </h6>
            <app-survey-custom-fields-filter
              [teamMemberCustomFields]="survey?.team_member_custom_fields ?? []"
              [members]="survey?.members"
              [filters]="filters"
              (filterChange)="onFilterChange()">
            </app-survey-custom-fields-filter>
          }
        </div>
      </div>
    </div>
  </div>
}

@if (loading) {
  <div class="my-4">
    <mat-progress-bar mode="indeterminate" color="primary"></mat-progress-bar>
  </div>
}

@if (survey?.answers_sent > 0 && filteredQuestions?.length > 0 && !loading) {
  <div class="mt-5">
    @if (tabIndex == 0) {
      @for (question of filteredQuestions; track question.id; let i = $index) {
        <div
          class="container-fluid accordion-container mt-3 accordion-inner answers-accordion"
          >
          <mat-accordion class="accordion" multi >
            <mat-expansion-panel [expanded]="true">
              <mat-expansion-panel-header class="py-2">
                <mat-panel-title>
                  <span>{{ question.question }}</span>
                </mat-panel-title>
              </mat-expansion-panel-header>
              <!-- <pre>{{ question | json }}</pre> -->
              <div class="container">

                @if (question?.description) {
                  <div class="alert alert-info my-3 mx-3 d-flex align-items-start" role="alert">
                    <mat-icon class="alert-info-icon flex-shrink-0 mr-2">info</mat-icon>
                    <div>
                      <strong>{{ 'generic.description' | translate }}:</strong>
                      <div class="description-html" [innerHTML]="question.description"></div>
                    </div>
                  </div>
                }
                <app-single-question-responses
                  [question]="question"
                  [showPrivateInfo]="showPrivateInfo"
                  [surveyId]="surveyId"
                  (aiAnalysisCompleted)="onAiAnalysisCompleted()"
                />
              </div>
            </mat-expansion-panel>
          </mat-accordion>
        </div>
      }
      <mat-paginator
        class="mt-5 paginator-rounded"
        [length]="totalQuestions"
        [pageSize]="pageSize"
        [pageIndex]="currentPageIndex"
        [pageSizeOptions]="[1, 5, 10]"
        (page)="onPageChange($event)">
      </mat-paginator>
    }
    @if (tabIndex == 1) {
    }
    @if (tabIndex == 2) {
    }
  </div>
}

@if (survey?.answers_sent > 0 && !survey?.customs?.anonymous_survey) {
  <div class="anonymize-button" (click)="toggleAnonymousData()">
    @if (showPrivateInfo) {
      <span>{{ 'generic.hide-private-data' | translate }}</span>
    }
    @if (!showPrivateInfo) {
      <span>{{ 'generic.show-private-data' | translate }}</span>
    }
  </div>
}
